import { useEffect, useRef } from 'preact/hooks'
import { useAthosContext } from 'libs/athos/fetchers/provider'
import { AthosSuggestProvider } from 'libs/athos/fetchers/suggest'
import { AthosTrendingProvider } from 'libs/athos/fetchers/trending'
import { SearchPageLink } from 'libs/athos/components/quick-search/SearchPageLink'
import { QuickSearchSuggestions } from 'libs/athos/components/quick-search/QuickSearchSuggestions'
import { QuickSearchResults } from 'libs/athos/components/quick-search/QuickSearchResults'
import { QuickSearchBlogSuggestions } from 'libs/athos/components/quick-search/QuickSearchBlogResults'
import clsx from 'clsx'
import { globalState } from '@/components/helpers/utils'
import { SkeletonCard } from 'libs/athos/components/SkeletonCard'
import { useSearchResultState } from '../../hooks/useSearchResultState'
import { GridRecommend } from 'libs/athos/components/recommendations/Recommendations'
import { calculateProductCount } from 'libs/athos/components/collection/filters/ProductCount'

/**
 * Gets popular categories HTML content from the DOM
 * @returns {string|undefined} HTML content for popular categories or undefined if not found
 */
const getPopularCategories = () => {
  const popularCategoryWrapper = document.getElementById('popular-categories')
  return popularCategoryWrapper?.innerHTML || undefined
}

// Extract and filter default suggestions from global state
const defaultSuggestions = globalState.search?.suggestions || []
const collectionsSuggestions = defaultSuggestions.filter(s => s.type === 'collections') || []
const pagesSuggestions = defaultSuggestions.filter(s => s.type === 'pages') || []
const popularProductsProfile = globalState.siteIds?.popularProductsProfile || 'api-test'

// Configuration for different search states
export const SEARCH_CONFIG = {
  NO_SEARCH: {
    suggestions: [
      { title: 'Popular Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: 'Popular Categories'
  },
  NO_RESULTS: {
    suggestions: [
      { title: 'Suggested Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: 'Popular Products'
  },
  HAS_RESULTS: {
    suggestions: [
      { title: 'Suggested Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Suggested Collections', type: 'collections-facets', default: collectionsSuggestions }
    ],
    mainTitle: 'Search Results'
  },
  REDIRECT: {
    mainTitle: 'Suggested Pages'
  }
}

/**
 * ViewResultsButton component - Renders the "View Results" button for search results
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @param {number} props.totalResults - Total number of search results
 * @returns {JSX.Element} Rendered component
 */
export const ViewResultsButton = ({ query, totalResults }) => {
  if (!query || !totalResults) return null

  return (
    <SearchPageLink query={query} className="btn btn-primary btn-fill btn-m w-full lg:w-fit lg:whitespace-nowrap">
      <span>
        View All {totalResults} Results
      </span>
    </SearchPageLink>
  )
}

/**
 * QuickSearchDropdown component - Displays search results and suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @returns {JSX.Element} Rendered component
 */
export function QuickSearchDropdown({ query, redirect, setRedirect, onResultsUpdate }) {
  const popularCategories = useRef(getPopularCategories())
  const { refine, results, isLoading } = useAthosContext()
  const { title, hasResults, showNoResults, isCheckingResults, isRecommendationsReady, isResultsReady } = useSearchResultState({ query, results, isLoading })
  const suggestedCollectionValues = results?.facets?.find(facet => facet.field === 'product_type')?.values?.slice(0, 3) || []

  const itemClass = 'w-full'
  const productCardSettings = {
    hideSwatches: false,
    hidePromolines: false,
    hideBadges: false
  }

  // Update search query when query prop changes
  useEffect(() => {
    refine('query', query)
  }, [query])

  useEffect(() => {
    if (results?.merchandising?.redirect) {
      setRedirect(results?.merchandising?.redirect)
    } else {
      setRedirect(false)
    }
  }, [results?.merchandising?.redirect])

  useEffect(() => {
    if (onResultsUpdate && results) {
      const totalResults = calculateProductCount(results)
      const hasResults = totalResults > 0
      onResultsUpdate({ hasResults, totalResults })
    }
  }, [results, onResultsUpdate])

  /**
   * Renders suggestion components based on current search state
   * @returns {JSX.Element[]} Array of SearchSuggestions components
   */
  const renderSuggestions = () => {
    let config
    if (query && hasResults) {
      config = SEARCH_CONFIG.HAS_RESULTS
    } else if (query && !hasResults) {
      config = SEARCH_CONFIG.NO_RESULTS
    } else {
      config = SEARCH_CONFIG.NO_SEARCH
    }
    const suggestionQuery = query && !hasResults ? '' : query
    return config.suggestions.map((suggestion, index) => {
      if (suggestion.type === 'pages' && query) {
        return (
          <div key={index} className="relative flex flex-col gap-y-1">
            <QuickSearchBlogSuggestions
              query={query}
              fallbackTitle={suggestion.title}
              fallbackSuggestions={suggestion.default}
              fallbackQuery={suggestionQuery}
            />
          </div>
        )
      }
      return (
        <SearchSuggestions
          key={index}
          title={suggestion.title}
          type={suggestion.type}
          query={suggestionQuery}
          collectionValues={suggestedCollectionValues}
          defaultSuggestions={suggestion.default}
          isStatic={suggestion.isStatic}
        />
      )
    })
  }

  /**
   * Renders the main content section based on search results
   * @returns {JSX.Element|null} QuickSearchResults or PopularCategories component
   */
  return (
    <div className="grid lg:grid-cols-main gap-x-gutter lg:gap-y-1.5 max-h-[75vh] bg-secondary lg:px-margin overflow-y-scroll no-scrollbar overscroll-y-none">
      {/* Main Content Section */}
      <div className="shrink-0 lg:col-[3/-1] lg:row-[1/2] flex flex-col gap-y-1 w-full lg:w-full-margin-half h-fit px-margin py-1 lg:py-2.5 overflow-x-hidden">
        <div className={clsx(
          'flex items-center justify-between gap-x-gutter w-full',
          hasResults && !redirect && ''
        )}>
          <div className={clsx(
            'flex flex-col gap-y-1 lg:gap-y-1.5 w-full',
            hasResults && !redirect && 'lg-max:sr-only'
          )}>
            {(!hasResults && query && !isLoading && showNoResults) && (
              <span className="lg-max:hidden w-full paragraph-s font-semibold pb-1 lg:pb-1.5 border-b border-borders-default">No search results for "{query}". Below are some suggested options.</span>
            )}
            <h2 className="paragraph-l font-extended font-semibold">{title}</h2>
          </div>

          {(hasResults && !redirect) && (
            <ViewResultsButton query={query} totalResults={calculateProductCount(results)} />
          )}
        </div>

        <div className="w-full">
          {/* Loading Overlay */}
          <div className={clsx('w-full overflow-hidden', (isCheckingResults || isLoading) ? '' : 'h-0 opacity-0 pointer-events-none')}>
            <SkeletonGrid itemClass={itemClass} />
          </div>
          <div className={clsx('w-full', (hasResults) ? '' : 'hidden')}>
            {isResultsReady && (
              <QuickSearchResults />
            )}
          </div>
          <div className={clsx('w-full', (!hasResults && query && !isLoading && showNoResults) ? '' : 'hidden')} data-dl-section-id="api-test">
            {isRecommendationsReady && (
              <GridRecommend profile={popularProductsProfile} limit={4} itemClass={itemClass} useCssGrid productCardSettings={productCardSettings} />
            )}
          </div>
          <div className={clsx('w-full', query ? 'hidden' : '')}>
            {popularCategories.current && <PopularCategories content={popularCategories.current} />}
          </div>
        </div>
      </div>

      {/* Suggestions Section */}
      <div className={clsx(
        'shrink-0 lg:col-[1/3] lg:row-[1/2] flex flex-col gap-y-1 w-full lg:w-full-margin-half h-fit lg:h-full px-margin lg:-ml-margin py-1 lg:py-2.5 lg:bg-page-secondary',
        !hasResults && 'lg-max:row-[1/2]'
      )}>
        {(!hasResults && query && !isLoading && showNoResults) && (
          <span className="lg:hidden w-full paragraph-s font-semibold pb-1 lg:pb-1.5 border-b border-borders-default">No search results for "{query}". Below are some suggested options.</span>
        )}
        <div className="grid grid-cols-2 lg:flex lg:flex-col gap-x-1.5 gap-y-2.5 w-full h-fit lg:h-full lg-max:px-margin lg-max:py-1 lg-max:bg-page-secondary">
          {renderSuggestions()}
        </div>
      </div>
    </div>
  )
}

/**
 * PopularCategories component - Renders HTML content for popular categories
 * @param {Object} props - Component props
 * @param {string} props.content - HTML content to render
 * @returns {JSX.Element} Rendered component
 */
const PopularCategories = ({ content }) => {
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-4 items-stretch gap-gutter w-full h-fit"
      dangerouslySetInnerHTML={{ __html: content }}
    />
  )
}

/**
 * SkeletonGrid component - Renders a loading state with skeleton cards
 * @param {Object} props - Component props
 * @param {string} props.itemClass - CSS class for grid items
 * @returns {JSX.Element} Rendered component
 */
const SkeletonGrid = ({ itemClass }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-gutter w-full h-fit">
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
      <SkeletonCard className={itemClass} />
    </div>
  )
}

/**
 * SearchSuggestions component - Renders search suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @param {string} props.type - Type of suggestions ('collections' or 'collections-facets')
 * @param {string} props.title - Title text for the suggestions section
 * @param {Array} props.defaultSuggestions - Default suggestions to display
 * @param {Array} props.collectionValues - Collection facet values for 'collections-facets' type
 * @param {boolean} props.isStatic - Whether to always render default suggestions (e.g. pages)
 * @returns {JSX.Element} Rendered component
 */
const SearchSuggestions = ({ query, type, title, defaultSuggestions, collectionValues = [], isStatic = false }) => {
  const isCollectionFacets = type === 'collections-facets'
  const hasCollectionValues = collectionValues.length > 0

  return (
    <div className="flex flex-col gap-y-1">
      <h3 className="paragraph-b font-extended font-semibold">{title}</h3>
      {isCollectionFacets && hasCollectionValues ? (
        <div className="flex flex-col gap-y-0.75 w-full">
          {collectionValues.map((item) => (
            <SearchPageLink
              key={`collection-${item.value}`}
              url={`/collections/${encodeURI(item.value.toLowerCase().replace(/\s+/g, '-'))}`}
              className="group flex w-fit paragraph-s link-fade"
            >
              <span>{item.label}</span>
            </SearchPageLink>
          ))}
        </div>
      ) : isCollectionFacets ? (
        <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} />
      ) : isStatic ? (
        <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} />
      ) : query ? (
        <AthosSuggestProvider category={type}>
          <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} />
        </AthosSuggestProvider>
      ) : (
        <AthosTrendingProvider category={type}>
          <QuickSearchSuggestions defaultSuggestions={defaultSuggestions} />
        </AthosTrendingProvider>
      )}
    </div>
  )
}
